import { useEffect, useCallback } from 'react';
import { useRouter } from 'next/router';
import { navLinks } from 'components/Navbar/navData';

// Poll for element and scroll when ready (snappy, no arbitrary delays)
const scrollToHashWhenReady = (hash, maxAttempts = 50) => {
  let attempts = 0;
  const tryScroll = () => {
    const element = document.querySelector(hash);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    } else if (attempts < maxAttempts) {
      attempts++;
      requestAnimationFrame(tryScroll);
    }
  };
  requestAnimationFrame(tryScroll);
};

export function useHotkeys() {
  const router = useRouter();

  const handleKeyDown = useCallback(
    event => {
      // Skip if user is typing in an input, textarea, or contenteditable
      const target = event.target;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      // Skip if modifier keys are pressed (allow browser shortcuts)
      if (event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }

      const key = event.key;

      // Key 1 for home/intro
      if (key === '1') {
        event.preventDefault();
        const scrollToIntro = () => {
          const element = document.querySelector('#intro');
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        };

        if (router.pathname !== '/') {
          router.push('/', undefined, { scroll: false }).then(() => {
            scrollToHashWhenReady('#intro');
          });
        } else {
          scrollToIntro();
        }
        return;
      }

      // Number keys 2-6 for main nav
      if (key >= '2' && key <= '6') {
        const index = parseInt(key, 10) - 2;
        if (index < navLinks.length) {
          event.preventDefault();
          const { pathname } = navLinks[index];

          if (pathname.startsWith('/#')) {
            // Hash link on home page - navigate to home then scroll
            const hash = pathname.slice(1);

            const scrollToElement = () => {
              const element = document.querySelector(hash);
              if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
              }
            };

            if (router.pathname !== '/') {
              // Navigate to home first, then scroll when element is ready
              router.push('/', undefined, { scroll: false }).then(() => {
                scrollToHashWhenReady(hash);
              });
            } else {
              scrollToElement();
            }
          } else {
            // Regular page navigation
            router.push(pathname, undefined, { scroll: false });
          }
        }
      }

      // Stack page hotkeys are now handled by the Stack component itself
      // using hash-based navigation (#hardware, #software, etc.)
    },
    [router]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}
